import * as RadixCheckbox from "@radix-ui/react-checkbox";
import { Check, Minus } from "@phosphor-icons/react";
import { forwardRef, useId, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twRing } from "@/lib/emotionTw";

export type CheckboxProps = RadixCheckbox.CheckboxProps & {
  label?: ReactNode;
  description?: ReactNode;
  error?: boolean;
  size?: "sm" | "md" | "lg";
};

type CheckboxSize = "sm" | "md" | "lg";

interface CheckboxOwnerState {
  size: CheckboxSize;
  error: boolean;
  disabled: boolean;
}

const indicatorSize = {
  sm: 9,
  md: 10,
  lg: 12
} as const;

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const CheckboxRoot = styled("div", {
  label: "AgenticCheckbox-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: CheckboxOwnerState }>(({ ownerState }) => [
  {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: ".5rem",
    fontSize: "12px",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.disabled
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
      }
    : { cursor: "pointer" }
]);

const checkboxControlBase: CSSObject = {
  marginTop: ".125rem",
  display: "flex",
  flexShrink: 0,
  cursor: "pointer",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-sm)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)"),
  "&:disabled": {
    cursor: "not-allowed",
    opacity: ".5"
  },
  '&[data-state="checked"]': {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
  },
  '&[data-state="indeterminate"]': {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
  }
};

const checkboxControlSize: Record<CheckboxSize, CSSObject> = {
  sm: { width: ".75rem", height: ".75rem", borderRadius: "2.5px", borderWidth: ".8px" },
  md: { width: ".875rem", height: ".875rem", borderRadius: "3px", borderWidth: "1px" },
  lg: { width: "1rem", height: "1rem", borderRadius: "4px", borderWidth: "1px" }
};

const CheckboxControl = styled(RadixCheckbox.Root, {
  label: "AgenticCheckbox-control",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: CheckboxOwnerState }>(({ ownerState }) => [
  checkboxControlBase,
  checkboxControlSize[ownerState.size],
  ownerState.error
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
      }
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-border-opacity, 1))"
      }
]);

const CheckboxLabel = styled("label", {
  label: "AgenticCheckbox-label",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: CheckboxOwnerState }>(({ ownerState }) => [
  {
    display: "grid",
    gap: ".125rem"
  },
  ownerState.disabled ? { cursor: "not-allowed" } : { cursor: "pointer" }
]);

const CheckboxLabelText = styled("span", {
  label: "AgenticCheckbox-labelText",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: CheckboxOwnerState }>(({ ownerState }) => [
  { fontWeight: 500 },
  ownerState.disabled
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
      }
]);

const CheckboxDescription = styled("span", {
  label: "AgenticCheckbox-description",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: CheckboxOwnerState }>(({ ownerState }) => [
  {
    fontSize: ".75rem",
    lineHeight: "1.25rem"
  },
  ownerState.disabled
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
      }
]);

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(({ className, label, description, size = "sm", error, ...props }, ref) => {
  const generatedId = useId();
  const checkboxId = (props.id as string | undefined) ?? generatedId;
  const hasContent = Boolean(label || description);
  const ownerState: CheckboxOwnerState = {
    size,
    error: Boolean(error),
    disabled: Boolean(props.disabled)
  };

  return (
    <CheckboxRoot ownerState={ownerState}>
      <CheckboxControl ref={ref} id={checkboxId} aria-invalid={error} ownerState={ownerState} className={className} {...props}>
        <RadixCheckbox.Indicator>
          {props.checked === "indeterminate" ? <Minus size={indicatorSize[size]} weight="bold" /> : <Check size={indicatorSize[size]} weight="bold" />}
        </RadixCheckbox.Indicator>
      </CheckboxControl>
      {hasContent && (
        <CheckboxLabel htmlFor={checkboxId} ownerState={ownerState}>
          {label && <CheckboxLabelText ownerState={ownerState}>{label}</CheckboxLabelText>}
          {description && <CheckboxDescription ownerState={ownerState}>{description}</CheckboxDescription>}
        </CheckboxLabel>
      )}
    </CheckboxRoot>
  );
});

Checkbox.displayName = "Checkbox";
